import type { ModuleFolderStructureItem, ModuleFolderStructureOutput, TemplateInfo } from "@scayla/api-client-fetch";
import { RecordBucket } from "@scayla/api-client-fetch";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";

import { $t } from "@/i18n";
import { localizeBuiltinModule } from "@/i18n/helpers";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";
import type { NavNode } from "@/utils/navUtils";

import { useNavigableModules } from "./modules";

/**
 * Every template of a module, wherever it sits. The endpoint returns the root ones in `templates` and
 * the rest inside the folder tree, so a module that keeps all of its templates in folders answers with
 * an empty root list. Both lists come back as null, not absent, on a leaf.
 */
function allTemplates(folders: ModuleFolderStructureItem[] | null | undefined): TemplateInfo[] {
  return (folders ?? []).flatMap((folder) => [...(folder.templates ?? []), ...allTemplates(folder.folders)]);
}

export interface ModuleFoldersStructure {
  moduleId: string;
  data: ModuleFolderStructureOutput;
}

// A user picks the module to inspect in the trash bin the same way they pick it in the regular
// module list, instead of from a hardcoded module triple. The folder trees are the caller's, already
// fetched for the module nav itself — nothing here may cost an extra request.
export function useBinNav(foldersStructure: MaybeRefOrGetter<ModuleFoldersStructure[]>) {
  const workspace = useWorkspace();
  const modules = useNavigableModules();

  const binModules = computed(() =>
    modules.value.filter((module) => {
      const structure = toValue(foldersStructure).find((item) => item.moduleId === module.id)?.data;
      const templates = [...(structure?.templates ?? []), ...allTemplates(structure?.folders)];
      return templates.some((template) => {
        const permissions = workspace.getTemplatePermissions(template.id);
        return permissions.read && permissions.delete;
      });
    }),
  );

  const nav = computed<NavNode | null>(() => {
    if (binModules.value.length === 0) return null;

    return {
      label: $t("trashBin.nav.label"),
      icon: "phosphor/trash",
      layoutStoreKey: "trashBin",
      children: binModules.value.map((module) => ({
        label: localizeBuiltinModule(module.builtinModule),
        layoutStoreKey: `_binModule_${module.id}`,
        canFavorite: false,
        route: { name: Routes.BIN, params: { moduleId: module.id } },
        activeRoutes: [
          { name: Routes.BIN, params: { moduleId: module.id } },
          { path: `/module/${module.id}`, query: { bucket: RecordBucket.DELETED } },
        ],
      })),
    };
  });

  return nav;
}
